import Element from 'ui/Element';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ClickableMixin from './mixins/ClickableMixin';
import LinkableMixin from './mixins/LinkableMixin';
import mixin from 'privates/utils/mixin';
import * as members from 'privates/privateMembers';
import vectorImageUtils from 'privates/utils/vectorImageUtils';
import {validate} from 'privates/validations/validation';
import isNil from 'lodash/isNil';

/**
 * @class VectorImage
 * @summary A simple vectorImage.
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.LinkableMixin
 * @mixes $w.ClickableMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.svgshape.SvgShape
 * @viewername wysiwyg.viewer.components.VectorImage
 * @tagname vectorImage
 */
class VectorImage extends Element {
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        super(RMI, compId, contextId, wixSelector, controllerId, role, type);
        mixin(VectorImage.prototype, HiddenCollapsedMixin.prototype);
        mixin(VectorImage.prototype, LinkableMixin.prototype);
        mixin(VectorImage.prototype, ClickableMixin.prototype);
    }

    /**
     * @member src
     * @label property
     * @syntax
     * get src(): string
     * set src(value: string): void
     * @summary Sets or gets the file location of the vector image.
     * @description
     *  Setting the `src` property changes the displayed vector image to
     *  the vector image found at the new `src` value.
     *
     *  Getting the `src` property returns the location of the
     *  current vector image file.
     *
     *  The vector image file is an image file from the <a href="https://support.wix.com/en/article/about-the-media-manager" target="_blank">Media Manager</a>.
     *
     *  The URL format is: `wix:vector://v1/<vector_uri>/<filename>`.
     * @snippet [VectorImage-src_get.es6=Get the vector image's location]
     * @snippet [VectorImage-src_set.es6=Set the vector image to be a vector image from the Media Manager]
     * @type {external:String}
     * @default An empty string
     * @memberof $w.VectorImage
     * @instance
     * @attr {external:String}
     */
    get src() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getData(compId) || {};

        return vectorImageUtils.buildFullUrlFromSvgId(compData.svgId, compData.title);
    }

    /**
     * @param {external:String} value
     * @ignore
     */
    set src(value) {
        const compType = members._type.get(this).compType;

        if (!validate(compType, 'src', {src: value})) {
            return;
        }

        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const placeholderSvgData = {svgId: '0da768_b05a1a324a9f4a2e9be9a9c5dc3f50c9.svg', title: ''};
        let svgData;

        if (isNil(value)) {
            svgData = placeholderSvgData;
        } else{
            svgData = vectorImageUtils.convertToModelFormatAndReportIfError(value, 'src', 'src');
            if (!svgData){
                svgData = placeholderSvgData;
            }
        }

        RMI.setData(compId, svgData);
    }

    toJSON() {
        const {id, type, global, rendered, src} = this;
        return {id, type, global, rendered, src};
    }
}

export default VectorImage;



// WEBPACK FOOTER //
// ./js/modules/ui/VectorImage.es6